import { createNewsletterClient } from './newsletter';
import type { HttpClient } from './httpClient';
import { isStubEnabled } from './httpClient';
import { wishlistQuery, type WishlistFilters, type WishlistPage, type WishlistSummary, type WishlistAdminItem } from './wishlists';

export interface Product {
  id: string;
  name?: string;
  title?: string;
  price?: number;
  priceMin?: number;
  priceMax?: number;
  slug?: string;
  description?: string;
  brandId?: string;
  categoryId?: string;
  labels?: Array<string | { id: string; name: string }>;
  isActive?: boolean;
  activateAtUtc?: string | null;
  storefrontSortOrder?: number;
  status?: 'active' | 'inactive';
  compareAtPrice?: number | null;
  trackInventory?: boolean;
  stockQty?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateProductInput {
  title?: string;
  name?: string;
  slug?: string;
  description?: string;
  shortDescription?: string;
  sku?: string;
  brandId?: string;
  categoryId?: string;
  labels?: string[];
  status?: 'active' | 'inactive';
  isActive?: boolean;
  activateAtUtc?: string | null;
  storefrontSortOrder?: number;
  defaultCurrency?: string;
  price?: number;
  compareAtPrice?: number | null;
  stockQty?: number;
  trackInventory?: boolean;
}

export interface Plan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
}

export type OrderSummary = {
  id: string;
  orderNo: string;
  customer: string;
  email: string;
  total: number;
  paymentStatus: 'paid' | 'pending' | 'refunded' | 'failed';
  fulfillmentStatus: 'unfulfilled' | 'partial' | 'fulfilled' | 'canceled';
  createdAt: string;
};

export type CustomerOrderSummary = {
  id: string;
  orderNo: string;
  total: number;
  paymentStatus: 'paid' | 'pending' | 'refunded' | 'failed' | 'awaiting_cod' | string;
  paymentMethod?: string;
  createdAt: string;
};

export type BarcodeSettings = {
  type: string;
  color: string;
  availableTypes?: Array<{ id: string; name: string; bwipId: string }>;
  availableColors?: string[];
  updatedAtUtc?: string | null;
};

export type BarcodeVariantListItem = {
  variantId: string;
  productId: string;
  productTitle: string;
  productSlug: string;
  variantSku: string;
  barcode: string;
  isActive: boolean;
  isDefault: boolean;
};

export type BarcodeVariantListResponse = {
  total: number;
  page: number;
  pageSize: number;
  items: BarcodeVariantListItem[];
};

export type RecentOrdersSettings = {
  enabled: boolean;
  limit: number;
  updatedAtUtc?: string | null;
};

const refundsStore: Map<string, Array<{ amount: number; at: string }>> = new Map();
const extraEventsStore: Map<string, Array<{ type: string; message: string; at: string }>> = new Map();
const memCoupons: any[] = [];
const memTaxRules: any[] = [];

type Customer = {
  id: string;
  name: string;
  email: string;
  phone?: string;
  tags?: string[];
  createdAt: string;
  ordersCount: number;
  updatedAt?: string;
  lastInvitedAt?: string;
  isDisabled?: boolean;
  marketing: { consent: boolean };
  address?: { shipping?: any; billing?: any };
  defaultPriceTier?: string;
};

function slugFromTitle(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replaceAll(/[^\da-z]+/g, '-')
    .replaceAll(/^-+|-+$/g, '') || 'product';
}

export function createAdminClient(client: HttpClient) {
  return {
    metrics: {
      async vitals(payload: { name: string; value: number; id: string; path: string; meta?: any }) {
        try { await client.post('/api/admin/metrics/vitals', payload as any); } catch { return { ok: false } as const; }
        return { ok: true } as const;
      },
      async errors(payload: {
        message: string;
        stack: string;
        path: string;
        ts?: string;
        release?: string;
        requestId?: string;
        tenantSlug?: string;
        route?: string;
        failingApi?: string | null;
        httpStatus?: number | null;
      }) {
        try { await client.post('/api/admin/metrics/errors', payload as any); } catch { return { ok: false } as const; }
        return { ok: true } as const;
      },
      async flags(payload: { key: string; variant: string | boolean; exposedAt?: string; user?: string | null }) {
        try { await client.post('/api/admin/metrics/flags', payload as any); } catch { return { ok: false } as const; }
        return { ok: true } as const;
      },
    },
    async listProducts(params?: Record<string, unknown>): Promise<{ items: any[]; total: number; page: number; pageSize: number }> {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params || {})) {
        if (v !== undefined && v !== null && `${v}` !== '') qs.set(k, String(v));
      }
      const url = `/api/admin/products${qs.toString() ? `?${qs}` : ''}`;
      try {
        const res = await client.get<{ items: any[]; total: number; page: number; pageSize: number }>(url);
        if (res && Array.isArray(res.items)) return res;
      } catch (error) {
        if (!isStubEnabled()) throw error as any;
      }

      // Stub dataset as fallback
      const fallback = [
        { id: 'demo-1', name: 'Demo: Sample Product', priceMin: 1999, priceMax: 1999, slug: 'sample-product', status: 'active', updatedAt: new Date().toISOString() },
        { id: 'demo-2', name: 'Demo: Another Product', priceMin: 2999, priceMax: 2999, slug: 'another-product', status: 'inactive', updatedAt: new Date(Date.now() - 86_400_000).toISOString() },
      ];
      return { items: fallback, total: fallback.length, page: 1, pageSize: fallback.length };
    },

    async createProduct(input: CreateProductInput): Promise<Product> {
      const title = input.title || input.name || 'New Product';
      const isActive = typeof input.isActive === 'boolean'
        ? input.isActive
        : input.status
          ? input.status === 'active'
          : true;
      const payload: any = {
        title,
        slug: input.slug,
        description: input.description,
        shortDescription: input.shortDescription,
        sku: input.sku,
        brandId: input.brandId,
        categoryId: input.categoryId,
        labels: input.labels,
        isActive,
        activateAtUtc: input.activateAtUtc,
        defaultCurrency: input.defaultCurrency,
      };

      const hasVariantFields =
        typeof input.price === 'number' ||
        typeof input.compareAtPrice === 'number' ||
        typeof input.stockQty === 'number' ||
        typeof input.trackInventory === 'boolean';
      if (hasVariantFields) {
        payload.variants = [
          {
            sku: input.sku || 'DEFAULT',
            price: input.price ?? 0,
            compareAtPrice: input.compareAtPrice,
            currency: input.defaultCurrency ?? 'USD',
            stockQuantity: input.stockQty ?? 0,
            trackInventory: input.trackInventory ?? false,
            isDefault: true,
            isActive: true,
          },
        ];
      }

      try {
        return await client.post<Product>('/api/admin/products', payload as any);
      } catch (error) {
        if (!isStubEnabled()) throw error as any;
        const now = new Date().toISOString();
        return {
          id: 'temp_' + Date.now(),
          name: title,
          title,
          slug: input.slug || slugFromTitle(title),
          description: input.description,
          isActive,
          status: isActive ? 'active' : 'inactive',
          price: input.price ?? 0,
          priceMin: input.price ?? 0,
          priceMax: input.price ?? 0,
          compareAtPrice: input.compareAtPrice ?? null,
          labels: input.labels,
          brandId: input.brandId,
          categoryId: input.categoryId,
          stockQty: input.stockQty ?? 0,
          trackInventory: input.trackInventory ?? false,
          createdAt: now,
          updatedAt: now,
        };
      }
    },

    async listPlans(): Promise<Plan[]> {
      // Stub implementation; wire real endpoint later
      try {
        return await client.get<Plan[]>('/api/admin/plans');
      } catch (error) {
        if (!isStubEnabled()) throw error as any;
        return [
          { id: 'p_basic', name: 'Basic', price: 990, interval: 'month' },
          { id: 'p_pro', name: 'Pro', price: 1990, interval: 'month' },
        ];
      }
    },

    async listOrders(filters?: {
      status?: 'open' | 'closed' | 'canceled';
      paymentStatus?: OrderSummary['paymentStatus'];
      fulfillmentStatus?: OrderSummary['fulfillmentStatus'];
      customer?: string;
      from?: string; // ISO date
      to?: string;   // ISO date
    }): Promise<OrderSummary[]> {
      try {
        const params = new URLSearchParams();
        if (filters?.status) params.set('status', filters.status);
        if (filters?.paymentStatus) params.set('paymentStatus', filters.paymentStatus);
        if (filters?.fulfillmentStatus) params.set('fulfillmentStatus', filters.fulfillmentStatus);
        if (filters?.customer) params.set('customer', filters.customer);
        if (filters?.from) params.set('from', filters.from);
        if (filters?.to) params.set('to', filters.to);
        return await client.get<OrderSummary[]>(`/api/admin/orders?${params.toString()}`);
      } catch (error) {
        if (!isStubEnabled()) throw error as any;
        const now = Date.now();
        const mock: OrderSummary[] = Array.from({ length: 20 }).map((_, i) => ({
          id: String(1000 + i),
          orderNo: `ORD-${1000 + i}`,
          customer: ['Alex Doe', 'Jamie Lin', 'Sam Park', 'Taylor Kim'][i % 4],
          email: ['alex@example.com', 'jamie@example.com', 'sam@example.com', 'taylor@example.com'][i % 4],
          total: Math.round((50 + (i % 5) * 25) * 100) / 100,
          paymentStatus: (['paid', 'pending', 'refunded', 'failed'] as const)[i % 4],
          fulfillmentStatus: (['unfulfilled', 'partial', 'fulfilled', 'canceled'] as const)[i % 4],
          createdAt: new Date(now - i * 86_400_000).toISOString(),
        }));
        let out = mock;
        if (filters?.customer) {
          const q = filters.customer.toLowerCase();
          out = out.filter((o) => o.customer.toLowerCase().includes(q) || o.email.toLowerCase().includes(q));
        }
        if (filters?.paymentStatus) out = out.filter((o) => o.paymentStatus === filters.paymentStatus);
        if (filters?.fulfillmentStatus) out = out.filter((o) => o.fulfillmentStatus === filters.fulfillmentStatus);
        if (filters?.from) out = out.filter((o) => new Date(o.createdAt) >= new Date(filters.from!));
        if (filters?.to) out = out.filter((o) => new Date(o.createdAt) <= new Date(filters.to!));
        return out;
      }
    },

    async markOrdersPaid(ids: string[]): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await client.post('/api/admin/orders/mark-paid', { ids });
        return { ok: true };
      } catch {
        return { ok: true };
      }
    },

    async cancelOrders(ids: string[], reason?: string): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await client.post('/api/admin/orders/cancel', { ids, reason });
        return { ok: true };
      } catch {
        return { ok: true };
      }
    },

    async getOrderById(id: string): Promise<{
      id: string;
      orderNo: string;
      tags: string[];
      customer: { name: string; email: string };
      address: { shipping: any; billing: any };
      items: Array<{ title: string; sku?: string; qty: number; price: number }>;
      payment: { status: 'paid' | 'pending' | 'refunded' | 'failed'; method?: string; subtotal: number; discount: number; shipping: number; tax: number; total: number };
      shipping: { method: string; status: 'unfulfilled' | 'partial' | 'fulfilled' | 'canceled' };
      createdAt: string;
    }> {
      try {
        return await client.get(`/api/admin/orders/${encodeURIComponent(id)}`);
      } catch {
        const subtotal = 89.97;
        const shipping = 7.5;
        const discount = 9;
        const tax = 6.5;
        return {
          id,
          orderNo: `ORD-${id}`,
          tags: ['priority'],
          customer: { name: 'Alex Doe', email: 'alex@example.com' },
          address: {
            shipping: { name: 'Alex Doe', address1: '1 Main St', city: 'Metropolis', state: 'CA', zip: '12345', country: 'US' },
            billing: { name: 'Alex Doe', address1: '1 Main St', city: 'Metropolis', state: 'CA', zip: '12345', country: 'US' },
          },
          items: [
            { title: 'T-Shirt', sku: 'TS-RED-M', qty: 1, price: 19.99 },
            { title: 'Hoodie', sku: 'HD-BLK-L', qty: 2, price: 34.99 },
          ],
          payment: { status: 'paid', method: 'mock', subtotal, discount, shipping, tax, total: subtotal - discount + shipping + tax },
          shipping: { method: 'Standard', status: 'fulfilled' },
          createdAt: new Date().toISOString(),
        };
      }
    },

    async getOrderTimeline(id: string): Promise<Array<{ type: string; message: string; at: string }>> {
      try {
        return await client.get(`/api/admin/orders/${encodeURIComponent(id)}/timeline`);
      } catch {
        const base = Date.now() - 1000 * 60 * 60 * 24 * 2;
        const events = [
          { type: 'placed', message: 'Order placed', at: new Date(base).toISOString() },
          { type: 'paid', message: 'Payment captured', at: new Date(base + 1000 * 60 * 30).toISOString() },
          { type: 'fulfilled', message: 'Items picked and packed', at: new Date(base + 1000 * 60 * 60 * 12).toISOString() },
          { type: 'shipped', message: 'Shipped via Standard', at: new Date(base + 1000 * 60 * 60 * 24).toISOString() },
        ];
        const refunds = refundsStore.get(id) || [];
        for (const r of refunds) {
          events.push({ type: 'refund', message: `Refunded $${r.amount.toFixed(2)}`, at: r.at });
        }
        const extra = extraEventsStore.get(id) || [];
        for (const e of extra) events.push(e);
        return events.sort((a, b) => +new Date(a.at) - +new Date(b.at));
      }
    },

    async listOrderNotes(id: string): Promise<Array<{ id: string; author: string; text: string; at: string }>> {
      try {
        return await client.get(`/api/admin/orders/${encodeURIComponent(id)}/notes`);
      } catch {
        return [
          { id: 'n1', author: 'Alex (admin)', text: 'Customer requested gift wrap.', at: new Date(Date.now() - 86_400_000).toISOString() },
        ];
      }
    },

    async addOrderNote(id: string, text: string): Promise<{ id: string; author: string; text: string; at: string }> {
      try {
        return await client.post(`/api/admin/orders/${encodeURIComponent(id)}/notes`, { text });
      } catch {
        return { id: Math.random().toString(36).slice(2, 8), author: 'You', text, at: new Date().toISOString() };
      }
    },

    async deleteOrderNote(id: string, noteId: string): Promise<{ ok: true }> {
      try { await client.post(`/api/admin/orders/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}/delete`); } catch { }
      return { ok: true };
    },

    async updateOrderTags(id: string, tags: string[]): Promise<{ ok: true }> {
      try { await client.post(`/api/admin/orders/${encodeURIComponent(id)}/tags`, { tags }); } catch { }
      return { ok: true };
    },

    async resendOrderEmail(id: string): Promise<{ ok: true }> {
      try { await client.post(`/api/admin/orders/${encodeURIComponent(id)}/resend-confirmation`); } catch { }
      return { ok: true };
    },

    async refundOrder(id: string, payload: { amount?: number; items?: Array<{ sku?: string; title?: string; qty: number; price: number }>; reason?: string }): Promise<{ ok: true; amount: number; refundId: string }> {
      const amount = typeof payload.amount === 'number' ? payload.amount : (payload.items || []).reduce((s, it) => s + (it.qty * it.price), 0);
      try { await client.post(`/api/admin/orders/${encodeURIComponent(id)}/refunds`, payload); } catch { }
      const at = new Date().toISOString();
      const arr = refundsStore.get(id) || [];
      arr.push({ amount, at });
      refundsStore.set(id, arr);
      return { ok: true, amount, refundId: 'rf_' + Math.random().toString(36).slice(2, 8) };
    },

    async getEligibleShippingMethods(orderId: string): Promise<Array<{ id: string; label: string; price: number }>> {
      try { return await client.get(`/api/admin/orders/${encodeURIComponent(orderId)}/eligible-shipping`); } catch {
        return [
          { id: 'standard', label: 'Standard (3–5 days)', price: 7.5 },
          { id: 'express', label: 'Express (1–2 days)', price: 15 },
          { id: 'overnight', label: 'Overnight', price: 29 },
        ];
      }
    },

    async updateOrderShipping(orderId: string, methodId: string): Promise<{ ok: true; shipping: { method: string; label: string; price: number }; payment: { subtotal: number; discount: number; shipping: number; tax: number; total: number } }> {
      try { return await client.post(`/api/admin/orders/${encodeURIComponent(orderId)}/shipping`, { methodId }); } catch {
        const methods = await (this as any).getEligibleShippingMethods(orderId);
        const m = methods.find((x: any) => x.id === methodId) || methods[0];
        const price = m?.price ?? 0;
        const subtotal = 89.97;
        const discount = 0;
        const tax = Math.round((subtotal - discount) * 0.1 * 100) / 100;
        const total = subtotal - discount + price + tax;
        const at = new Date().toISOString();
        const arr = extraEventsStore.get(orderId) || [];
        arr.push({ type: 'shipping_changed', message: `Shipping method changed to ${m?.label || methodId}`, at });
        extraEventsStore.set(orderId, arr);
        return { ok: true, shipping: { method: methodId, label: m?.label || methodId, price }, payment: { subtotal, discount, shipping: price, tax, total } } as any;
      }
    },

    // Analytics
    analytics: {
      async kpis(params: { from: string; to: string; tz?: string; compare?: '1' | undefined }): Promise<{
        data: {
          revenue: { value: number; prev: number; delta: number };
          orders: { value: number; prev: number; delta: number };
          aov: { value: number; prev: number; delta: number };
          conversion: { value: number; prev: number; delta: number };
        };
      }> {
        try {
          const qs = new URLSearchParams();
          if (params.from) qs.set('from', params.from);
          if (params.to) qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          if (params.compare) qs.set('compare', params.compare);
          const data = await client.get(`/api/admin/analytics/kpis?${qs.toString()}`);
          return data as any;
        } catch {
          // Stub: generate deterministic KPIs based on date range length
          const from = new Date(params.from + 'T00:00:00');
          const to = new Date(params.to + 'T00:00:00');
          const days = Math.max(1, Math.floor((to.getTime() - from.getTime()) / 86_400_000) + 1);
          const seed = days + (params.tz?.length || 0);
          const rand = (n: number) => Math.abs(Math.sin(seed * 12.9898 + n) * 43_758.5453) % 1;
          const revenue = Math.round((500 + days * 120 + rand(1) * 1000) * 100) / 100;
          const orders = Math.max(1, Math.floor(10 + days * 3 + rand(2) * 30));
          const aov = Math.round((revenue / orders) * 100) / 100;
          const conversion = Math.round((2 + rand(3) * 3) * 100) / 100; // 2-5%
          const prevFactor = 0.9 + rand(4) * 0.2; // +/-10%
          const prevRevenue = Math.round(revenue * prevFactor * 100) / 100;
          const prevOrders = Math.max(1, Math.floor(orders * prevFactor));
          const prevAov = Math.round((prevRevenue / prevOrders) * 100) / 100;
          const prevConversion = Math.round(conversion * prevFactor * 100) / 100;
          const pct = (v: number, p: number) => Math.round(((v - p) / (p || 1)) * 100);
          return {
            data: {
              revenue: { value: revenue, prev: prevRevenue, delta: pct(revenue, prevRevenue) },
              orders: { value: orders, prev: prevOrders, delta: pct(orders, prevOrders) },
              aov: { value: aov, prev: prevAov, delta: pct(aov, prevAov) },
              conversion: { value: conversion, prev: prevConversion, delta: pct(conversion, prevConversion) },
            },
          } as any;
        }
      },

      async timeseries(params: {
        from: string;
        to: string;
        tz?: string;
        sample?: 'hour' | 'day' | 'week';
        compareFrom?: string;
        compareTo?: string;
      }): Promise<{
        data: {
          primary: Array<{ date: string; revenue: number; orders: number }>;
          compare?: Array<{ date: string; revenue: number; orders: number }>;
        };
      }> {
        try {
          const qs = new URLSearchParams();
          qs.set('from', params.from);
          qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          if (params.sample) qs.set('sample', params.sample);
          if (params.compareFrom) qs.set('compareFrom', params.compareFrom);
          if (params.compareTo) qs.set('compareTo', params.compareTo);
          const data = await client.get(`/api/admin/analytics/timeseries?${qs.toString()}`);
          return data as any;
        } catch {
          const makeSeries = (fromStr: string, toStr: string, sample: 'hour' | 'day' | 'week') => {
            const out: Array<{ date: string; revenue: number; orders: number }> = [];
            let start = new Date(fromStr + (sample === 'hour' ? '' : 'T00:00:00'));
            const end = new Date(toStr + (sample === 'hour' ? '' : 'T00:00:00'));
            const stepMs = sample === 'hour' ? 3_600_000 : (sample === 'day' ? 86_400_000 : 7 * 86_400_000);
            // Normalize weekly start to Monday
            if (sample === 'week') {
              const day = start.getUTCDay() || 7; // 1..7
              start = new Date(start.getTime() - (day - 1) * 86_400_000);
            }
            let i = 0;
            for (let t = start.getTime(); t <= end.getTime(); t += stepMs) {
              const dt = new Date(t);
              const label = sample === 'hour'
                ? dt.toISOString().slice(0, 13) + ':00'
                : dt.toISOString().slice(0, 10);
              const base = Math.abs(Math.sin(t / 8.64e7)) + 0.1 * Math.cos(t / 3.6e6);
              const orders = Math.max(0, Math.round(10 + base * 15 + (i % 5)));
              const revenue = Math.round((orders * (20 + (i % 7) * 5 + (base * 10))) * 100) / 100;
              out.push({ date: label, revenue, orders });
              i++;
            }
            return out;
          };
          // Decide sampling by range
          const from = new Date(params.from + 'T00:00:00');
          const to = new Date(params.to + 'T00:00:00');
          const days = Math.max(1, Math.floor((to.getTime() - from.getTime()) / 86_400_000) + 1);
          const sample: 'hour' | 'day' | 'week' = params.sample || (days <= 2 ? 'hour' : (days <= 90 ? 'day' : 'week'));
          const primary = makeSeries(params.from, params.to, sample);
          const compare = params.compareFrom && params.compareTo ? makeSeries(params.compareFrom, params.compareTo, sample) : undefined;
          return { data: { primary, compare } } as any;
        }
      },

      async sources(params: {
        from: string;
        to: string;
        tz?: string;
        medium?: string;
        campaign?: string;
      }): Promise<Array<{
        source: string;
        medium: string;
        campaign: string;
        sessions: number;
        users: number;
        orders: number;
        revenue: number;
        conv: number; // percent
        aov: number;
        bounce: number; // percent
      }>> {
        try {
          const qs = new URLSearchParams();
          qs.set('from', params.from);
          qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          if (params.medium) qs.set('medium', params.medium);
          if (params.campaign) qs.set('campaign', params.campaign);
          return await client.get(`/api/admin/analytics/sources?${qs.toString()}`);
        } catch {
          const mediums = ['cpc', 'email', 'social', 'referral', 'organic'] as const;
          const sourcesByMedium: Record<string, string[]> = {
            cpc: ['google', 'bing', 'meta'],
            email: ['campaign_monitor', 'mailchimp'],
            social: ['twitter', 'instagram', 'facebook'],
            referral: ['partnerA', 'partnerB', 'blogX'],
            organic: ['google', 'bing', 'yahoo'],
          } as any;
          const campaigns = ['spring_sale', 'launch', 'retargeting', 'newsletter', 'brand'] as const;
          const seed = (+new Date(params.from + 'T00:00:00')) ^ (+new Date(params.to + 'T00:00:00'));
          const rnd = (n: number) => Math.abs(Math.sin(seed * 0.0001 + n) * 10_000) % 1;
          let rows: any[] = [];
          let idx = 0;
          for (const m of mediums) {
            for (const s of (sourcesByMedium[m] || ['unknown'])) {
              for (const c of campaigns) {
                const base = 100 + Math.floor(rnd(idx++) * 900);
                const sessions = base + Math.floor(rnd(idx++) * 200);
                const users = Math.max(1, Math.floor(sessions * (0.7 + rnd(idx++) * 0.2)));
                const orders = Math.max(0, Math.floor(sessions * (0.01 + rnd(idx++) * 0.05)));
                const revenue = Math.round((orders * (30 + rnd(idx++) * 120)) * 100) / 100;
                const conv = Math.round(((orders / (sessions || 1)) * 100) * 100) / 100;
                const aov = Math.round(((revenue / (orders || 1)) || 0) * 100) / 100;
                const bounce = Math.round((40 + rnd(idx++) * 40) * 100) / 100; // 40-80%
                rows.push({ source: s, medium: m, campaign: c, sessions, users, orders, revenue, conv, aov, bounce });
              }
            }
          }
          if (params.medium) rows = rows.filter((r) => r.medium === params.medium);
          if (params.campaign) rows = rows.filter((r) => r.campaign.toLowerCase().includes(params.campaign!.toLowerCase()));
          return rows.slice(0, 200);
        }
      },

      async pages(params: {
        from: string;
        to: string;
        tz?: string;
        landing?: '1' | undefined;
      }): Promise<Array<{
        page: string;
        path: string;
        views: number;
        entrances: number;
        exits: number;
        avgTime: string;
        orders: number;
        revenue: number;
        cmsId?: string;
      }>> {
        try {
          const qs = new URLSearchParams();
          qs.set('from', params.from);
          qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          if (params.landing) qs.set('landing', params.landing);
          return await client.get(`/api/admin/analytics/pages?${qs.toString()}`);
        } catch {
          const pages = [
            { page: 'Home', path: '/', cmsId: 'home' },
            { page: 'About Us', path: '/about', cmsId: 'about' },
            { page: 'Contact', path: '/contact', cmsId: 'contact' },
            { page: 'Landing - Spring', path: '/landing/spring' },
            { page: 'Landing - Campaign', path: '/landing/campaign-1' },
            { page: 'Blog - Post 1', path: '/blog/post-1' },
            { page: 'Blog - Post 2', path: '/blog/post-2' },
          ];
          const seed = (+new Date(params.from + 'T00:00:00')) ^ (+new Date(params.to + 'T00:00:00'));
          const rnd = (n: number) => Math.abs(Math.sin(seed * 0.000_13 + n) * 10_000) % 1;
          const fmtTime = (secs: number) => {
            const h = Math.floor(secs / 3600).toString().padStart(2, '0');
            const m = Math.floor((secs % 3600) / 60).toString().padStart(2, '0');
            const s = Math.floor(secs % 60).toString().padStart(2, '0');
            return `${h}:${m}:${s}`;
          };
          let rows = pages.map((p, i) => {
            const views = 500 + Math.floor(rnd(i) * 5000);
            const entrances = Math.floor(views * (0.4 + rnd(i + 1) * 0.5));
            const exits = Math.floor(views * (0.2 + rnd(i + 2) * 0.5));
            const avgSecs = 30 + rnd(i + 3) * 240;
            const orders = Math.floor(views * (0.005 + rnd(i + 4) * 0.02));
            const revenue = Math.round((orders * (40 + rnd(i + 5) * 120)) * 100) / 100;
            return { page: p.page, path: p.path, cmsId: p.cmsId, views, entrances, exits, avgTime: fmtTime(avgSecs), orders, revenue };
          });
          if (params.landing === '1') {
            rows = rows.filter((r) => r.path.startsWith('/landing') || r.path === '/');
          }
          return rows;
        }
      },

      async funnel(params: {
        from: string;
        to: string;
        tz?: string;
        events: string[];
      }): Promise<Array<{ event: string; label: string; count: number; stepConv: number; cumulativeConv: number }>> {
        try {
          const qs = new URLSearchParams();
          qs.set('from', params.from);
          qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          if (params.events?.length) qs.set('events', params.events.join(','));
          return await client.get(`/api/admin/analytics/funnel?${qs.toString()}`);
        } catch {
          // Stub: generate a deterministic funnel with decreasing counts
          const labels: Record<string, string> = {
            session_start: 'Session',
            page_view: 'Page View',
            view_product: 'PDP View',
            add_to_cart: 'Add to Cart',
            checkout_start: 'Checkout',
            purchase: 'Purchase',
          };
          const seed = (+new Date(params.from + 'T00:00:00')) ^ (+new Date(params.to + 'T00:00:00')) ^ (params.events?.length || 0);
          const rnd = (n: number) => (Math.abs(Math.sin(seed * 0.000_071 + n) * 10_000) % 1);
          const start = 10_000 + Math.floor(rnd(1) * 50_000);
          const steps: Array<{ event: string; label: string; count: number; stepConv: number; cumulativeConv: number }> = [];
          let prev = start;
          for (const [i, ev] of params.events.entries()) {
            const drop = 0.2 + rnd(i + 2) * 0.6; // 20%-80% retention per step
            const count = i === 0 ? start : Math.max(0, Math.floor(prev * drop));
            const stepConv = i === 0 ? 100 : ((count / (prev || 1)) * 100);
            const cumulativeConv = (count / (start || 1)) * 100;
            steps.push({ event: ev, label: labels[ev] || ev, count, stepConv, cumulativeConv });
            prev = count;
          }
          return steps;
        }
      },

      async cohort(params: {
        from: string;
        to: string;
        tz?: string;
      }): Promise<Array<{
        cohort: string;
        total: number;
        cells: Array<{ week: string; weekOffset: number; rate: number; count: number }>;
      }>> {
        try {
          const qs = new URLSearchParams();
          qs.set('from', params.from);
          qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          return await client.get(`/api/admin/analytics/cohort?${qs.toString()}`);
        } catch {
          // Stub: generate cohorts by week with decreasing repeat purchase rate
          const start = new Date(params.from + 'T00:00:00');
          const end = new Date(params.to + 'T00:00:00');
          // Get Monday of start week
          const startDay = start.getUTCDay() || 7;
          const week0 = new Date(start.getTime() - (startDay - 1) * 86_400_000);
          const weeksCount = Math.max(1, Math.ceil((end.getTime() - week0.getTime()) / (7 * 86_400_000)) + 1);
          const seed = week0.getTime() ^ end.getTime();
          const rnd = (n: number) => Math.abs(Math.sin(seed * 0.000_019 + n) * 10_000) % 1;
          const rows: any[] = [];
          const maxCols = Math.min(12, weeksCount); // up to 12 columns
          for (let w = 0; w < weeksCount; w++) {
            const cohortStart = new Date(week0.getTime() + w * 7 * 86_400_000);
            const label = cohortStart.toISOString().slice(0, 10);
            const total = 50 + Math.floor(rnd(w + 1) * 950);
            const cells: any[] = [];
            for (let c = 0; c < maxCols; c++) {
              const base = 35 - c * (5 + rnd(c + w) * 2); // decreasing base percent
              const rate = Math.max(0, Math.min(100, Math.round((base + rnd(c + 2 * w) * 10) * 10) / 10));
              const count = Math.round((rate / 100) * total);
              const weekLabel = new Date(cohortStart.getTime() + c * 7 * 86_400_000).toISOString().slice(0, 10);
              cells.push({ week: weekLabel, weekOffset: c, rate, count });
            }
            rows.push({ cohort: label, total, cells });
          }
          return rows;
        }
      },

      async products(params: {
        from: string;
        to: string;
        tz?: string;
      }): Promise<{ top: Array<{ id: string; name: string; views: number; units: number; revenue: number; conv: number }>; bottom: Array<{ id: string; name: string; views: number; units: number; revenue: number; conv: number }> }> {
        try {
          const qs = new URLSearchParams();
          qs.set('from', params.from);
          qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          return await client.get(`/api/admin/analytics/products?${qs.toString()}`);
        } catch {
          const seed = (+new Date(params.from + 'T00:00:00')) ^ (+new Date(params.to + 'T00:00:00'));
          const rnd = (n: number) => Math.abs(Math.sin(seed * 0.000_031 + n) * 10_000) % 1;
          const rows: Array<{ id: string; name: string; views: number; units: number; revenue: number; conv: number }> = [];
          const N = 60;
          for (let i = 1; i <= N; i++) {
            const views = 200 + Math.floor(rnd(i) * 9800);
            const baseConv = 0.002 + rnd(i + 1) * 0.06; // 0.2% - 6.2%
            const units = Math.max(0, Math.floor(views * baseConv));
            const price = 10 + Math.floor(rnd(i + 2) * 190);
            const revenue = Math.round(units * price * 100) / 100;
            const conv = Math.round(((units / (views || 1)) * 100) * 10) / 10;
            rows.push({ id: String(i), name: `Product ${i}`, views, units, revenue, conv });
          }
          const top = [...rows].sort((a, b) => b.revenue - a.revenue).slice(0, 10);
          const bottom = [...rows]
            .filter((r) => r.views > 0)
            .sort((a, b) => a.conv - b.conv || b.views - a.views)
            .slice(0, 10);
          return { top, bottom } as any;
        }
      },

      async customers(params: {
        from: string;
        to: string;
        tz?: string;
        rfm?: string; // e.g., '111', '311'
        hasEmail?: '1' | undefined;
        subscribed?: '1' | undefined;
      }): Promise<Array<{
        id: string;
        name: string;
        email?: string;
        orders: number;
        revenue: number;
        lastOrder: string;
        rfm: string;
        subscribed: boolean;
      }>> {
        try {
          const qs = new URLSearchParams();
          qs.set('from', params.from);
          qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          if (params.rfm) qs.set('rfm', params.rfm);
          if (params.hasEmail) qs.set('hasEmail', params.hasEmail);
          if (params.subscribed) qs.set('subscribed', params.subscribed);
          return await client.get(`/api/admin/analytics/customers?${qs.toString()}`);
        } catch {
          const seed = (+new Date(params.from + 'T00:00:00')) ^ (+new Date(params.to + 'T00:00:00'));
          const rnd = (n: number) => Math.abs(Math.sin(seed * 0.000_047 + n) * 10_000) % 1;
          const bands = ['111', '211', '311', '411', '511', '611', '711', '811', '911', '933', '999'];
          let rows: any[] = [];
          const N = 120;
          for (let i = 1; i <= N; i++) {
            const orders = Math.floor(1 + rnd(i) * 12);
            const revenue = Math.round((orders * (20 + rnd(i + 1) * 180)) * 100) / 100;
            const last = new Date(Date.now() - Math.floor(rnd(i + 2) * 120) * 86_400_000).toISOString();
            const rfm = bands[Math.floor(rnd(i + 3) * bands.length)] || '999';
            const hasEmail = rnd(i + 4) > 0.2;
            const subscribed = hasEmail && rnd(i + 5) > 0.5;
            rows.push({ id: String(1000 + i), name: `Customer ${i}`, email: hasEmail ? `user${i}@example.com` : undefined, orders, revenue, lastOrder: last, rfm, subscribed });
          }
          if (params.rfm) rows = rows.filter((r) => r.rfm === params.rfm);
          if (params.hasEmail) rows = rows.filter((r) => !!r.email);
          if (params.subscribed) rows = rows.filter((r) => r.subscribed);
          return rows;
        }
      },

      async activity(params: {
        from: string;
        to: string;
        tz?: string;
        actor?: string;
        entity?: string; // id
        type?: string; // entity type
        date?: string; // specific day
      }): Promise<Array<{
        id: string;
        time: string;
        actor: string;
        action: string;
        entityType: string;
        entityId: string;
        metadata: any;
      }>> {
        try {
          const qs = new URLSearchParams();
          qs.set('from', params.from);
          qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          if (params.actor) qs.set('actor', params.actor);
          if (params.entity) qs.set('entity', params.entity);
          if (params.type) qs.set('type', params.type);
          if (params.date) qs.set('date', params.date);
          return await client.get(`/api/admin/analytics/activity?${qs.toString()}`);
        } catch {
          // Stub: generate a mixed activity feed
          const seed = (+new Date(params.from + 'T00:00:00')) ^ (+new Date(params.to + 'T00:00:00'));
          const rnd = (n: number) => Math.abs(Math.sin(seed * 0.000_057 + n) * 10_000) % 1;
          const actors = ['alex', 'jamie', 'sam', 'taylor', 'system'];
          const types = ['order', 'customer', 'product'] as const;
          const actions = {
            order: ['created', 'paid', 'refunded', 'fulfilled'],
            customer: ['created', 'updated', 'subscribed', 'unsubscribed'],
            product: ['created', 'updated', 'activated', 'deactivated'],
          } as const;
          const rows: any[] = [];
          const fromMs = +new Date(params.from + 'T00:00:00');
          const toMs = +new Date(params.to + 'T23:59:59');
          const total = 80;
          for (let i = 0; i < total; i++) {
            const t = new Date(fromMs + Math.floor(rnd(i) * (toMs - fromMs)));
            const entityType = types[Math.floor(rnd(i + 1) * types.length)] as any;
            const actionArr = (actions as any)[entityType] as string[];
            const action = actionArr[Math.floor(rnd(i + 2) * actionArr.length)];
            const id = String(1000 + Math.floor(rnd(i + 3) * 9000));
            const actor = actors[Math.floor(rnd(i + 4) * actors.length)];
            const metadata = entityType === 'order'
              ? { total: Math.round((20 + rnd(i + 5) * 200) * 100) / 100, currency: 'USD', items: Math.floor(1 + rnd(i + 6) * 5) }
              : (entityType === 'customer'
                ? { email: `${actor}${id.slice(-2)}@example.com`, marketing: rnd(i + 7) > 0.5 }
                : { name: `Product ${id}`, price: Math.round((10 + rnd(i + 8) * 190) * 100) / 100 });
            rows.push({ id: `evt_${i}`, time: t.toISOString(), actor, action, entityType, entityId: id, metadata });
          }
          let out = rows.sort((a, b) => +new Date(b.time) - +new Date(a.time));
          if (params.actor) out = out.filter((r) => r.actor.toLowerCase().includes(params.actor!.toLowerCase()));
          if (params.entity) out = out.filter((r) => r.entityId.includes(params.entity!));
          if (params.type) out = out.filter((r) => r.entityType === params.type);
          if (params.date) {
            const day = params.date;
            out = out.filter((r) => r.time.startsWith(day));
          }
          return out;
        }
      },

      async experiments(params: {
        from: string;
        to: string;
        tz?: string;
      }): Promise<Array<{
        id: string;
        name: string;
        status: 'active' | 'paused' | 'completed';
        primaryMetric: string;
        lift?: number;
        pValue?: number;
        variants: Array<{ name: string; users: number; percent: number }>;
      }>> {
        try {
          const qs = new URLSearchParams();
          qs.set('from', params.from);
          qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          return await client.get(`/api/admin/analytics/experiments?${qs.toString()}`);
        } catch {
          const seed = (+new Date(params.from + 'T00:00:00')) ^ (+new Date(params.to + 'T00:00:00'));
          const rnd = (n: number) => Math.abs(Math.sin(seed * 0.000_063 + n) * 10_000) % 1;
          const list = Array.from({ length: 8 }).map((_, i) => {
            const id = `exp_${100 + i}`;
            const status: any = (['active', 'paused', 'completed'] as const)[Math.floor(rnd(i) * 3)];
            const variants = ['A', 'B', rnd(i + 1) > 0.6 ? 'C' : null].filter(Boolean) as string[];
            const users = variants.map((_, vi) => 500 + Math.floor(rnd(i + vi + 2) * 5000));
            const total = users.reduce((s, n) => s + n, 0);
            const variantRows = variants.map((name, vi) => ({ name, users: users[vi], percent: Math.round((users[vi] / total) * 1000) / 10 }));
            const primaryMetric = (['Conversion', 'Revenue/Visitor', 'Signup Rate'] as const)[Math.floor(rnd(i + 3) * 3)];
            const lift = Math.round(((rnd(i + 4) - 0.5) * 20) * 100) / 100; // +/-10%
            const pValue = status === 'completed' ? Math.round(rnd(i + 5) * 1000) / 1000 : undefined;
            return { id, name: `Experiment ${i + 1}`, status, primaryMetric, lift, pValue, variants: variantRows };
          });
          return list;
        }
      },
      async experimentDetail(key: string, params: { from: string; to: string; tz?: string }): Promise<{
        status: 'active' | 'paused' | 'completed';
        metrics: Array<{ name: string; users: number; conversion: number; revenue: number; delta?: number; ciLow?: number; ciHigh?: number; bounce?: number; latency?: number }>;
      }> {
        try {
          const qs = new URLSearchParams();
          qs.set('from', params.from);
          qs.set('to', params.to);
          if (params.tz) qs.set('tz', params.tz);
          return await client.get(`/api/admin/analytics/experiments/${encodeURIComponent(key)}?${qs.toString()}`);
        } catch {
          const seed = (+new Date(params.from + 'T00:00:00')) ^ (+new Date(params.to + 'T00:00:00')) ^ key.length;
          const rnd = (n: number) => Math.abs(Math.sin(seed * 0.000_071 + n) * 10_000) % 1;
          const variants = ['A', 'B', rnd(3) > 0.7 ? 'C' : null].filter(Boolean) as string[];
          const users = variants.map((_, i) => 800 + Math.floor(rnd(i + 1) * 4000));
          const baseConv = 2 + rnd(2) * 4; // 2-6%
          const baseRev = 20 + rnd(4) * 120;
          const metrics = variants.map((name, i) => {
            const conv = Math.max(0.5, baseConv + (rnd(i + 5) - 0.5) * 2);
            const rev = Math.max(5, baseRev + (rnd(i + 6) - 0.5) * 20);
            const delta = i === 0 ? 0 : Math.round(((conv - baseConv) / (baseConv || 1)) * 1000) / 10;
            const ciLow = i === 0 ? undefined : Math.max(0, conv - (rnd(i + 7) * 1.5));
            const ciHigh = i === 0 ? undefined : conv + (rnd(i + 8) * 1.5);
            const bounce = 30 + rnd(i + 9) * 40;
            const latency = 0.4 + rnd(i + 10) * 1.2;
            return { name, users: users[i], conversion: conv, revenue: rev, delta, ciLow, ciHigh, bounce, latency };
          });
          const status: any = (['active', 'paused', 'completed'] as const)[Math.floor(rnd(11) * 3)];
          return { status, metrics } as any;
        }
      },
    },

    // Customers
    async listCustomers(params?: { q?: string; sortBy?: 'createdAt' | 'ordersCount'; sortDir?: 'asc' | 'desc'; createdFrom?: string; createdTo?: string; page?: number; pageSize?: number }): Promise<{ items: Customer[]; total: number }> {
      const qs = new URLSearchParams();
      if (params?.q) qs.set('q', params.q);
      if (params?.sortBy) qs.set('sortBy', params.sortBy);
      if (params?.sortDir) qs.set('sortDir', params.sortDir);
      if (params?.page) qs.set('page', String(params.page));
      if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
      return await client.get(`/api/admin/customers?${qs.toString()}`);
    },

    async createCustomer(input: { name: string; email: string; phone?: string; tags?: string[]; address?: { shipping?: any; billing?: any }; defaultPriceTier?: string }): Promise<Customer> {
      return await client.post('/api/admin/customers', input as any);
    },

    async getCustomerById(id: string): Promise<Customer> {
      return await client.get(`/api/admin/customers/${encodeURIComponent(id)}`);
    },

    async updateCustomer(id: string, patch: Partial<Customer>): Promise<Customer> {
      return await client.post(`/api/admin/customers/${encodeURIComponent(id)}`, patch);
    },

    async updateCustomerMarketing(id: string, consent: boolean): Promise<{ ok: true }> {
      await client.post(`/api/admin/customers/${encodeURIComponent(id)}/marketing`, { consent });
      return { ok: true };
    },

    async inviteCustomer(id: string): Promise<{ ok: true; invitedAtUtc?: string; token?: string }> {
      return await client.post(`/api/admin/customers/${encodeURIComponent(id)}/invite`);
    },

    async disableCustomer(id: string): Promise<{ ok: true }> {
      return await client.post(`/api/admin/customers/${encodeURIComponent(id)}/disable`);
    },

    async listCustomerOrders(id: string): Promise<CustomerOrderSummary[]> {
      return await client.get(`/api/admin/customers/${encodeURIComponent(id)}/orders`);
    },

    async listCustomerNotes(id: string): Promise<Array<{ id: string; author: string; text: string; at: string }>> {
      return await client.get(`/api/admin/customers/${encodeURIComponent(id)}/notes`);
    },
    async addCustomerNote(id: string, text: string): Promise<{ id: string; author: string; text: string; at: string }> {
      return await client.post(`/api/admin/customers/${encodeURIComponent(id)}/notes`, { text });
    },
    async deleteCustomerNote(id: string, noteId: string): Promise<{ ok: true }> {
      return await client.post(`/api/admin/customers/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}/delete`);
    },

    // Wishlists & Newsletter
    async listWishlists(filters: WishlistFilters = {}): Promise<WishlistPage<WishlistSummary>> {
      return client.get(`/api/admin/wishlists?${wishlistQuery(filters)}`);
    },

    async listWishlistItems(customerId: string, filters: WishlistFilters = {}): Promise<WishlistPage<WishlistAdminItem>> {
      return client.get(`/api/admin/wishlists/${encodeURIComponent(customerId)}/items?${wishlistQuery(filters)}`);
    },

    async removeWishlistItem(customerId: string, itemId: string): Promise<{ ok: true }> {
      return client.post(`/api/admin/wishlists/${encodeURIComponent(customerId)}/items/${encodeURIComponent(itemId)}/remove`);
    },

    ...createNewsletterClient(client),

    async generateMissingBarcodes(): Promise<{ count: number }> {
      return await client.post('/api/admin/barcodes/generate-missing', {});
    },

    // Product Barcodes
    async getBarcodeSettings(): Promise<BarcodeSettings> {
      try { return await client.get('/api/admin/barcodes/settings'); } catch (error) {
        if (!isStubEnabled()) throw error as any;
      }
      return {
        type: 'CODE128',
        color: 'black',
        availableTypes: [{ id: 'CODE128', name: 'Code 128', bwipId: 'code128' }, { id: 'PHARMA', name: 'Pharmacode', bwipId: 'pharmacode' }, { id: 'UPCA', name: 'UPC-A', bwipId: 'upca' }],
        availableColors: ['black', 'blue', 'brown', 'green', 'yellow', 'orange'],
        updatedAtUtc: new Date().toISOString(),
      };
    },

    async updateBarcodeSettings(type: string, color: string): Promise<BarcodeSettings> {
      try { return await client.post('/api/admin/barcodes/settings', { type, color } as any); } catch (error) {
        if (!isStubEnabled()) throw error as any;
      }
      return {
        type,
        color,
        availableTypes: [{ id: 'CODE128', name: 'Code 128', bwipId: 'code128' }, { id: 'PHARMA', name: 'Pharmacode', bwipId: 'pharmacode' }, { id: 'UPCA', name: 'UPC-A', bwipId: 'upca' }],
        availableColors: ['black', 'blue', 'brown', 'green', 'yellow', 'orange'],
        updatedAtUtc: new Date().toISOString(),
      };
    },

    async listBarcodeVariants(params?: { q?: string; page?: number; pageSize?: number; hasBarcode?: boolean; isActive?: boolean }): Promise<BarcodeVariantListResponse> {
      const qs = new URLSearchParams();
      if (params?.q) qs.set('q', params.q);
      if (params?.page) qs.set('page', String(params.page));
      if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
      if (params?.hasBarcode !== undefined) qs.set("hasBarcode", String(params.hasBarcode));
      if (params?.isActive !== undefined) qs.set("isActive", String(params.isActive));
      const url = `/api/admin/barcodes/variants${qs.toString() ? `?${qs}` : ''}`;
      try { return await client.get(url); } catch (error) {
        if (!isStubEnabled()) throw error as any;
      }

      const page = params?.page && params.page > 0 ? params.page : 1;
      const pageSize = params?.pageSize && params.pageSize > 0 ? Math.min(params.pageSize, 200) : 50;
      const q = (params?.q || '').trim().toLowerCase();

      const all: BarcodeVariantListItem[] = Array.from({ length: 80 }).map((_, i) => ({
        variantId: `var_${i}`,
        productId: `p_${Math.floor(i / 2)}`,
        productTitle: `Product ${Math.floor(i / 2) + 1}`,
        productSlug: `product-${Math.floor(i / 2) + 1}`,
        variantSku: `SKU-${Math.floor(i / 2) + 1}-${(i % 2) + 1}`,
        barcode: String(10_000_000 + i),
        isActive: true,
        isDefault: i % 2 === 0,
      }));

      const filtered = q
        ? all.filter((x) =>
          x.productTitle.toLowerCase().includes(q)
          || x.productSlug.toLowerCase().includes(q)
          || x.variantSku.toLowerCase().includes(q)
          || x.barcode.toLowerCase().includes(q))
        : all;

      const total = filtered.length;
      const items = filtered.slice((page - 1) * pageSize, page * pageSize);
      return { total, page, pageSize, items };
    },

    // Recent Orders
    async getRecentOrdersSettings(): Promise<RecentOrdersSettings> {
      try { return await client.get('/api/admin/recent-orders/settings'); } catch (error) {
        if (!isStubEnabled()) throw error as any;
      }
      return { enabled: false, limit: 5, updatedAtUtc: new Date().toISOString() };
    },

    async updateRecentOrdersSettings(enabled: boolean, limit: number): Promise<RecentOrdersSettings> {
      try { return await client.post('/api/admin/recent-orders/settings', { enabled, limit } as any); } catch (error) {
        if (!isStubEnabled()) throw error as any;
      }
      return { enabled, limit, updatedAtUtc: new Date().toISOString() };
    },

    // Coupons CRUD (in-memory store backing)
    async listCoupons(): Promise<Array<{ id: string; code: string; type: 'percent' | 'fixed' | 'free_ship'; value?: number; start?: string | null; end?: string | null; usageLimit?: number | null; perCustomerLimit?: number | null; status?: 'active' | 'disabled' }>> {
      try { return await client.get('/api/admin/coupons'); } catch { return memCoupons; }
    },
    async createCoupon(input: { code: string; type: 'percent' | 'fixed' | 'free_ship'; value?: number; start?: string | null; end?: string | null; usageLimit?: number | null; perCustomerLimit?: number | null; status?: 'active' | 'disabled' }): Promise<any> {
      try { return await client.post('/api/admin/coupons', input as any); } catch {
        const code = (input.code || '').toUpperCase();
        if (!code) throw new Error('Code required');
        if (memCoupons.some((c) => c.code === code)) throw new Error('Coupon exists');
        const c = { id: 'cp_' + Math.random().toString(36).slice(2, 8), ...input, code } as any;
        memCoupons.push(c);
        return c;
      }
    },
    async updateCoupon(id: string, patch: Partial<{ code: string; type: 'percent' | 'fixed' | 'free_ship'; value?: number; start?: string | null; end?: string | null; usageLimit?: number | null; perCustomerLimit?: number | null; status?: 'active' | 'disabled' }>): Promise<any> {
      try { return await client.post(`/api/admin/coupons/${encodeURIComponent(id)}`, patch); } catch {
        const idx = memCoupons.findIndex((c) => c.id === id);
        if (idx === -1) throw new Error('Not found');
        const code = (patch.code ?? memCoupons[idx].code).toUpperCase();
        memCoupons[idx] = { ...memCoupons[idx], ...patch, code } as any;
        return memCoupons[idx];
      }
    },
    async deleteCoupon(id: string): Promise<{ ok: true }> {
      try { await client.post(`/api/admin/coupons/${encodeURIComponent(id)}/delete`, {}); } catch { }
      const idx = memCoupons.findIndex((c) => c.id === id);
      if (idx !== -1) memCoupons.splice(idx, 1);
      return { ok: true };
    },

    // Tax rules CRUD (in-memory)
    async listTaxRules(): Promise<Array<{ id: string; country: string; rate: number; mode: 'inclusive' | 'exclusive' }>> {
      try { return await client.get('/api/admin/taxes'); } catch { return memTaxRules; }
    },
    async createTaxRule(input: { country: string; rate: number; mode: 'inclusive' | 'exclusive' }): Promise<any> {
      try { return await client.post('/api/admin/taxes', input as any); } catch {
        const r = { id: 'tx_' + Math.random().toString(36).slice(2, 8), ...input, country: (input.country || '').toUpperCase() } as any;
        memTaxRules.push(r);
        return r;
      }
    },
    async updateTaxRule(id: string, patch: Partial<{ country: string; rate: number; mode: 'inclusive' | 'exclusive' }>): Promise<any> {
      try { return await client.post(`/api/admin/taxes/${encodeURIComponent(id)}`, patch); } catch {
        const idx = memTaxRules.findIndex((t) => t.id === id);
        if (idx === -1) throw new Error('Not found');
        memTaxRules[idx] = { ...memTaxRules[idx], ...patch, country: (patch.country ?? memTaxRules[idx].country).toUpperCase() } as any;
        return memTaxRules[idx];
      }
    },
    async deleteTaxRule(id: string): Promise<{ ok: true }> {
      try { await client.post(`/api/admin/taxes/${encodeURIComponent(id)}/delete`, {}); } catch { }
      const idx = memTaxRules.findIndex((t) => t.id === id);
      if (idx !== -1) memTaxRules.splice(idx, 1);
      return { ok: true };
    },

    async createPagePreviewToken(id: string): Promise<{ token: string; expiresAtUtc?: string; version?: number }> {
      return await client.post(`/api/admin/pages/${encodeURIComponent(id)}/preview-token`, {});
    },
  };
}


